import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Label } from './label';
import { Badge } from './badge';
import { Check, Plus, Edit2, Trash2, Palette, Users, User } from 'lucide-react';
import type { ColorPalette, ColorPaletteWithPermissions } from '@shared/schemas/colorPalette';
import { getBuiltInColorPalettes } from '../../lib/colorPalettes';
import { colorPaletteService } from '../../services/colorPaletteService';
import CustomPaletteCreator from './CustomPaletteCreator';

interface ColorPaletteSelectorProps {
  selectedPaletteId: string;
  onPaletteChange: (paletteId: string) => void;
  userId: string;
  organizationId: string;
  showManagementButtons?: boolean;
  className?: string;
}

const ColorPaletteSelector: React.FC<ColorPaletteSelectorProps> = ({
  selectedPaletteId,
  onPaletteChange,
  userId,
  organizationId,
  showManagementButtons = true,
  className = '',
}) => {
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editingPalette, setEditingPalette] = useState<ColorPalette | null>(null);
  const [deletingPalette, setDeletingPalette] = useState<string | null>(null);
  const [userPalettes, setUserPalettes] = useState<ColorPaletteWithPermissions[]>([]);
  const [orgPalettes, setOrgPalettes] = useState<ColorPaletteWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);

  const builtInPalettes = getBuiltInColorPalettes();

  // Load palettes on mount and when user/org changes
  useEffect(() => {
    loadPalettes();
  }, [userId, organizationId]);

  const loadPalettes = async () => {
    try {
      setLoading(true);
      
      const { userPalettes: userPals, organizationPalettes: orgPals } = 
        await colorPaletteService.getAccessiblePalettes(userId, organizationId);
      
      setUserPalettes(userPals);
      setOrgPalettes(orgPals);
    } catch (error) {
      console.error('Error loading palettes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePalette = () => {
    setEditingPalette(null);
    setCreatorOpen(true);
  };

  const handleEditPalette = (palette: ColorPalette) => {
    setEditingPalette(palette);
    setCreatorOpen(true);
  };

  const handleSavePalette = async (palette: ColorPalette) => {
    // Reload palettes to get the latest data
    await loadPalettes();
  };

  const handleDeletePalette = async (paletteId: string) => {
    try {
      setDeletingPalette(paletteId);
      await colorPaletteService.delete(paletteId, userId);
      
      // If we deleted the currently selected palette, switch to default
      if (selectedPaletteId === paletteId) {
        onPaletteChange('fireforge');
      }
      
      // Reload palettes
      await loadPalettes();
    } catch (error) {
      console.error('Error deleting palette:', error);
    } finally {
      setDeletingPalette(null);
    }
  };

  const PaletteCard: React.FC<{ palette: any; isCustom?: boolean; permissions?: any }> = ({ 
    palette, 
    isCustom = false, 
    permissions 
  }) => {
    const isSelected = selectedPaletteId === palette.id;
    
    return (
      <div 
        className={`relative p-3 border rounded-lg cursor-pointer transition-all ${
          isSelected 
            ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
            : 'border-border hover:border-border-hover'
        }`}
        onClick={() => onPaletteChange(palette.id)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{palette.name}</span>
            {isCustom && permissions?.canUpdate && (
              <User className="h-3 w-3 text-muted-foreground" />
            )}
            {isCustom && !permissions?.canUpdate && (
              <Users className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          
          {isSelected && (
            <Check className="h-4 w-4 text-primary flex-shrink-0" />
          )}
        </div>
        
        <div className="flex gap-0.5 mb-2">
          {palette.colors.slice(0, 6).map((color: string, index: number) => (
            <div
              key={index}
              className="h-4 flex-1 rounded-sm border border-border/20"
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
          {palette.colors.length > 6 && (
            <div className="h-4 w-4 rounded-sm bg-muted border border-border/20 flex items-center justify-center">
              <span className="text-xs text-muted-foreground">+{palette.colors.length - 6}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {palette.colors.length} colors
          </span>
          
          {isCustom && permissions && showManagementButtons && (
            <div className="flex items-center gap-1">
              {permissions.canUpdate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditPalette(palette);
                  }}
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              
              {permissions.canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePalette(palette.id);
                  }}
                  disabled={deletingPalette === palette.id}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>
        
        {palette.description && (
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {palette.description}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-end items-center justify-end">
        {/* <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium">Default Color Palette</h3>
        </div> */}
        {showManagementButtons && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCreatePalette}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Custom
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {!loading && (
        <>
          {/* Built-in Palettes */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-muted-foreground">Built-in Palettes</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {builtInPalettes.map((palette) => (
                <PaletteCard key={palette.id} palette={palette} />
              ))}
            </div>
          </div>

          {/* User's Own Palettes */}
          {userPalettes.length > 0 && (
            <div className="space-y-3 mt-14">
              <h5 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Your Palettes
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {userPalettes.map((palette) => (
                  <PaletteCard 
                    key={palette.id} 
                    palette={palette} 
                    isCustom={true}
                    permissions={palette.permissions}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Organization Palettes */}
          {orgPalettes.length > 0 && (
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Organization Palettes
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {orgPalettes.map((palette) => (
                  <PaletteCard 
                    key={palette.id} 
                    palette={palette} 
                    isCustom={true}
                    permissions={palette.permissions}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {userPalettes.length === 0 && orgPalettes.length === 0 && showManagementButtons && (
            <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
              <Palette className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <h5 className="text-sm font-medium mb-1">No custom palettes yet</h5>
              <p className="text-xs text-muted-foreground mb-3">
                Create your own color palettes to match your brand or preferences
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreatePalette}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Your First Palette
              </Button>
            </div>
          )}
        </>
      )}

      {/* Creator Dialog */}
      <CustomPaletteCreator
        isOpen={creatorOpen}
        onClose={() => {
          setCreatorOpen(false);
          setEditingPalette(null);
        }}
        onSave={handleSavePalette}
        editingPalette={editingPalette}
        userId={userId}
        organizationId={organizationId}
      />
    </div>
  );
};

export default ColorPaletteSelector; 